export const INDEX_BEAT_STALE_MS = 60_000;

export type DocumentIndexProgressView = {
  percent: number;
  label: string;
  eta: string | null;
  waiting: boolean;
  remainingMs: number | null;
  fullMs: number | null;
};

export function formatIndexEta(remainingMs: number): string {
  if (remainingMs < 5_000) {
    return "ещё несколько секунд";
  }
  if (remainingMs < 45_000) {
    const seconds = Math.max(10, Math.round(remainingMs / 10_000) * 10);
    return `ещё ~${seconds} с`;
  }
  if (remainingMs < 90 * 60 * 1000) {
    const minutes = Math.max(1, Math.round(remainingMs / 60_000));
    return `ещё ~${minutes} мин`;
  }
  const totalMinutes = Math.max(1, Math.round(remainingMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) {
    return `ещё ~${hours} ч`;
  }
  return `ещё ~${hours} ч ${minutes} мин`;
}

export function remainingIndexMs(input: {
  done: number;
  total: number;
  stageAt: Date | string | null;
  now?: Date;
}): number | null {
  if (input.done < 1 || input.total <= input.done || !input.stageAt) {
    return null;
  }
  const started = new Date(input.stageAt).getTime();
  if (Number.isNaN(started)) {
    return null;
  }
  const elapsed = (input.now ?? new Date()).getTime() - started;
  if (elapsed < 1_000) {
    return null;
  }
  return (elapsed / input.done) * (input.total - input.done);
}

export function isIndexBeatStale(
  beatAt: Date | string | null | undefined,
  now?: Date,
): boolean {
  if (!beatAt) {
    return true;
  }
  const beat = new Date(beatAt).getTime();
  if (Number.isNaN(beat)) {
    return true;
  }
  return (now ?? new Date()).getTime() - beat > INDEX_BEAT_STALE_MS;
}

/** Progress of the current stage only (0–100), not a full-pipeline offset. */
export function stageUnitPercent(done: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((done / total) * 100));
}

function withEta(
  view: Omit<DocumentIndexProgressView, "eta" | "remainingMs" | "fullMs">,
  remaining: number | null,
  done: number,
  total: number,
): DocumentIndexProgressView {
  const fullMs =
    remaining != null && total > done && done >= 1
      ? remaining * (total / (total - done))
      : null;
  return {
    ...view,
    eta: remaining == null ? null : formatIndexEta(remaining),
    remainingMs: remaining,
    fullMs,
  };
}

function liveIndexProgress(input: {
  parseStatus: string;
  indexStage: string | null;
  indexDone: number;
  indexTotal: number;
  indexStageAt: Date | string | null;
  jobRunning?: boolean;
  now?: Date;
}): DocumentIndexProgressView {
  const remaining = remainingIndexMs({
    done: input.indexDone,
    total: input.indexTotal,
    stageAt: input.indexStageAt,
    now: input.now,
  });

  if (input.indexStage === "EMBED") {
    return withEta(
      {
        percent: stageUnitPercent(input.indexDone, input.indexTotal),
        label:
          input.indexTotal > 0
            ? `Поиск ${input.indexDone}/${input.indexTotal}`
            : "Поиск",
        waiting: false,
      },
      remaining,
      input.indexDone,
      input.indexTotal,
    );
  }

  if (input.indexStage === "EXTRACT") {
    return withEta(
      {
        percent: stageUnitPercent(input.indexDone, input.indexTotal),
        label:
          input.indexTotal > 0
            ? `Wiki ${input.indexDone}/${input.indexTotal}`
            : "Wiki",
        waiting: false,
      },
      remaining,
      input.indexDone,
      input.indexTotal,
    );
  }

  if (
    input.indexStage === "PARSE" ||
    input.parseStatus === "PROCESSING" ||
    input.jobRunning
  ) {
    return {
      percent: 0,
      label: "Разбор",
      eta: null,
      waiting: false,
      remainingMs: null,
      fullMs: null,
    };
  }

  return {
    percent: 0,
    label: "В очереди",
    eta: null,
    waiting: true,
    remainingMs: null,
    fullMs: null,
  };
}

export function documentIndexProgress(input: {
  status: string;
  parseStatus: string;
  indexStage: string | null;
  indexDone: number;
  indexTotal: number;
  indexStageAt: Date | string | null;
  indexBeatAt?: Date | string | null;
  jobRunning?: boolean;
  now?: Date;
}): DocumentIndexProgressView | null {
  if (input.status === "FAILED") {
    return null;
  }

  const followup =
    input.status === "PROCESSING" ||
    Boolean(input.jobRunning) ||
    input.indexStage != null;
  if (!followup) {
    return null;
  }

  const live = liveIndexProgress(input);
  if (live.label === "В очереди") {
    return input.status === "PROCESSING" ? live : null;
  }
  if (
    isIndexBeatStale(input.indexBeatAt, input.now) &&
    (input.indexBeatAt || live.label !== "Разбор")
  ) {
    // Keep real stage counts on the bar; only rename when nothing has started yet.
    if (input.indexDone < 1) {
      return {
        percent: live.percent,
        label: "Ожидание воркера",
        eta: null,
        waiting: false,
        remainingMs: null,
        fullMs: null,
      };
    }
    return {
      ...live,
      eta: null,
      remainingMs: null,
      fullMs: null,
    };
  }
  return live;
}
